import React, { useState } from "react";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import CommonModal from "../../ResetContext/components/DumpModal";
import { useUpdatePaymentMethod } from "../../../../../queries/billing";
import toast from "react-hot-toast";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Loader2 } from "lucide-react";

// You should get your publishable key from env or props

const PaymentForm = ({ onClose, agency, userActiveLocation }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const { mutateAsync: updatePaymentMethod } = useUpdatePaymentMethod(userActiveLocation?.id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!stripe || !elements) {
      setLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    // Create payment method with billing details
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: {
        name,
        email,
      },
    });

    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }

    try {
      const data = await updatePaymentMethod({
        id: paymentMethod.id,
        agency_id: agency?._id,
        customer_id: userActiveLocation?.customer_id,
      });
      if (data?.data) {
        toast.success("Payment method updated successfully!");
        setLoading(false);
        onClose();
        return;
      }
    } catch (err) {
      // fall through to the generic error toast below
    }
    toast.error("Something went wrong");
    setLoading(false);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label className="block text-sm mb-1">Name</label>
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>
      <div className="mb-3">
        <label className="block text-sm mb-1">Email</label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div style={{ margin: "16px 0" }}>
        <CardElement options={{ hidePostalCode: true }} />
      </div>
      {error && <div className="text-destructive">{error}</div>}
      <Button type="submit" disabled={!stripe || loading} className="w-full mt-2">
        {loading && <Loader2 className="animate-spin" />}
        {loading ? "Saving..." : "Save Payment Method"}
      </Button>
    </form>
  );
};

const EditPaymentMethod = ({
  isOpen,
  onClose,
  title,
  agency,
  userActiveLocation,
}) => {
  if (!agency?.stripe?.publish_key) {
    return (
      <CommonModal
        title={title}
        showAlert={false}
        isOpen={isOpen}
        onClose={onClose}
      >
        <div className="p-4">
          <p className="text-destructive">Stripe publish key is not available.</p>
        </div>
      </CommonModal>
    );
  }
  const stripePromise = loadStripe(agency?.stripe?.publish_key);
  return (
    <CommonModal
      title={title}
      showAlert={false}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Elements stripe={stripePromise}>
        <PaymentForm
          onClose={onClose}
          agency={agency}
          userActiveLocation={userActiveLocation}
        />
      </Elements>
    </CommonModal>
  );
};

export default EditPaymentMethod;
